import { uniq } from "lodash";
import { IconType } from "react-icons";
import {
  RiCheckFill,
  RiCheckLine,
  RiCloseCircleLine,
  RiDeleteBinLine,
  RiEditLine,
  RiLogoutBoxLine,
  RiLogoutBoxRLine,
  RiTimeLine,
} from "react-icons/ri";
import { StatusTagMetadata } from "../components/DataDisplay/StatusTag";
import * as GQL from "../types/graphql";

/**
 * The maximum number of simultaneous chunked queries to run.  This matches
 * Apollo's default batch size of 10.
 */
export const CHUNKED_BATCH_SIZE = 10;

export const STATUS_ACTIVE = "Active";
export const STATUS_CLOSED = "Closed";
export const STATUS_INACTIVE = "Inactive";

export const ELEVATED_SCHOOL_ADMIN_GROUP_NAME = "elevated_school_admin";

export type MultiSelectOptionType = {
  id?: string;
  value: string;
  label: string;
};

export const PAGINATION_DEFAULT_LIMIT = 25;
export const PAGINATION_DEFAULT_OFFSET = 0;

interface FormStatusTagMetadata extends StatusTagMetadata {
  menuItemDescription?: string;
  menuItemSelectedDescription?: string;
  statusDialogHeader?: string;
  statusDialogDescription?: string;
}

export type FormStatusType = {
  label: string;
  longLabel: string;
  icon?: IconType;
  variant?: string;
  colorScheme: string;
  accentColor?: string;
  menuItemDescription?: string;
  menuItemSelectedDescription?: string;
  statusDialogHeader?: string;
  statusDialogDescription?: string;
  bulkStatusDialogHeader?: (count: number) => string;
  bulkStatusDialogDescription?: (count: number) => string;
  isActive: boolean;
};

function bulkFormNoun(count: number): string {
  return count === 1 ? "form" : "forms";
}

function bulkFormResponses(count: number): {
  subject: string;
  object: string;
  verbHave: string;
} {
  if (count === 1) {
    return {
      subject: "the form response",
      object: "it",
      verbHave: "it has",
    };
  }
  return {
    subject: "these form responses",
    object: "them",
    verbHave: "they have",
  };
}

export function getBulkFormStatusDialogCopy(
  status: GQL.form_status_enum,
  count: number
): { header: string; body: string } {
  const copy = FORM_STATUS[status];
  if (copy.bulkStatusDialogHeader && copy.bulkStatusDialogDescription) {
    return {
      header: copy.bulkStatusDialogHeader(count),
      body: copy.bulkStatusDialogDescription(count),
    };
  }
  const { subject } = bulkFormResponses(count);
  return {
    header: `Update status on ${count} ${bulkFormNoun(count)}?`,
    body: `This will change the status of ${subject}.`,
  };
}

type FormStatusTypes = Record<GQL.form_status_enum, FormStatusType>;

type PermissionLevelsTypes = Record<
  GQL.person_type_enum,
  FormStatusTagMetadata
>;

export const FORM_STATUS: FormStatusTypes = {
  [GQL.form_status_enum.InProgress]: {
    label: "In progress",
    longLabel: "Form response in progress",
    icon: RiEditLine,
    colorScheme: "orange",
    menuItemDescription: "Form response in progress and not submitted",
    menuItemSelectedDescription: "Currently in progress",
    statusDialogHeader: "Revert form response?",
    statusDialogDescription:
      "Reverting the form response to “In progress” will allow it to be edited and submitted.",
    bulkStatusDialogHeader: (count) =>
      `Revert ${count} ${bulkFormNoun(count)}?`,
    bulkStatusDialogDescription: (count) => {
      const { subject, object } = bulkFormResponses(count);
      return `Reverting ${subject} to “In progress” will allow ${object} to be edited and submitted.`;
    },
    isActive: true,
  },
  [GQL.form_status_enum.Submitted]: {
    label: "Submitted",
    longLabel: "Form response submitted",
    icon: RiCheckFill,
    colorScheme: "green",
    menuItemDescription: "Submit this form response",
    menuItemSelectedDescription: "Currently submitted",
    statusDialogHeader: "Submit form response?",
    statusDialogDescription:
      "Submitting the form response will enter it into the lottery process.",
    bulkStatusDialogHeader: (count) =>
      `Submit ${count} ${bulkFormNoun(count)}?`,
    bulkStatusDialogDescription: (count) => {
      const { subject, object } = bulkFormResponses(count);
      return `Submitting ${subject} will enter ${object} into the lottery process.`;
    },
    isActive: true,
  },
  [GQL.form_status_enum.Cancelled]: {
    label: "Canceled",
    longLabel: "Form response canceled",
    icon: RiCloseCircleLine,
    colorScheme: "gray",
    menuItemDescription: "Withdraw form response from lottery",
    menuItemSelectedDescription: "Currently withdrawn from lottery",
    statusDialogHeader: "Cancel form response?",
    statusDialogDescription:
      "Canceling the form response will withdraw it from the lottery process.",
    bulkStatusDialogHeader: (count) =>
      `Cancel ${count} ${bulkFormNoun(count)}?`,
    bulkStatusDialogDescription: (count) => {
      const { subject, object } = bulkFormResponses(count);
      return `Canceling ${subject} will withdraw ${object} from the lottery process.`;
    },
    isActive: true,
  },
  [GQL.form_status_enum.Deleted]: {
    // This is actually won't be used, we just need this to satisfy the type system.
    label: "Deleted",
    longLabel: "Form response deleted",
    icon: RiDeleteBinLine,
    colorScheme: "red",
    isActive: false,
  },
  [GQL.form_status_enum.Withdrawn]: {
    label: "Withdrawn",
    longLabel: "Form response withdrawn",
    icon: RiLogoutBoxLine,
    colorScheme: "gray",
    menuItemDescription: "Parent/guardian only",
    menuItemSelectedDescription: "Currently withdrawn by the parent/guardian",
    isActive: false,
  },
  [GQL.form_status_enum.Verified]: {
    label: "Verified",
    longLabel: "Form response verified",
    icon: RiCheckFill,
    colorScheme: "blue",
    accentColor: "#3182CE",
    menuItemDescription: "Verify and prevent changes from parents",
    menuItemSelectedDescription:
      "Fully verified, parents can't edit form response",
    statusDialogHeader: "Verify form response?",
    statusDialogDescription:
      "Verifying the form response indicates that it has been reviewed and approved. It will also partially prevent it from being edited by the parent/guardian.",
    bulkStatusDialogHeader: (count) =>
      `Verify ${count} ${bulkFormNoun(count)}?`,
    bulkStatusDialogDescription: (count) => {
      const { subject, object, verbHave } = bulkFormResponses(count);
      return `Verifying ${subject} indicates that ${verbHave} been reviewed and approved. It will also partially prevent ${object} from being edited by the parent/guardian.`;
    },
    isActive: true,
  },
  [GQL.form_status_enum.LotteryReady]: {
    label: "Ready for lottery",
    longLabel: "Form response ready for lottery",
    icon: RiCheckFill,
    colorScheme: "purple",
    accentColor: "#805AD5",
    menuItemDescription: "Approve for lottery and prevent changes from parents",
    menuItemSelectedDescription:
      "Approved for lottery, parents can't edit the form response",
    statusDialogHeader: "Set form response as ready for lottery?",
    statusDialogDescription:
      "Setting the form response as ready for lottery will approve it for the lottery process. It will also prevent it from being edited by the parent/guardian.",
    bulkStatusDialogHeader: (count) =>
      `Set ${count} ${bulkFormNoun(count)} as ready for lottery?`,
    bulkStatusDialogDescription: (count) => {
      const { subject, object } = bulkFormResponses(count);
      return `Setting ${subject} as ready for lottery will approve ${object} for the lottery process. It will also prevent ${object} from being edited by the parent/guardian.`;
    },
    isActive: true,
  },
  [GQL.form_status_enum.Admissions]: {
    label: "Admissions",
    longLabel: "Admissions",
    icon: RiLogoutBoxRLine,
    colorScheme: "blue",
    accentColor: "#63B3ED",
    menuItemDescription: "Form response in lottery and offers",
    menuItemSelectedDescription: "Currently in admissions",
    statusDialogHeader: "Set form response as admission?",
    statusDialogDescription: "Please update me here.",
    isActive: true,
  },
};

export const LOTTERY_AND_OFFERS_DISABLED_MESSAGE =
  "To activate this message, first turn on Lottery & Offers in Settings";

// for internal categorization
export enum FilterTypes {
  Status = "status",
  SubmittedBefore = "submittedBefore",
  School = "school",
  AttendingSchool = "attendingSchool",
  Rank = "rank",
  Tags = "tags",
  VerificationStatus = "verificationStatus",
  DynamicQuestions = "dynamicQuestions",
  Grades = "grades",
  Badges = "badges",
  SubStatus = "subStatus",
  WithinCapacity = "withinCapacity",
  PreferredLanguage = "preferredLanguage",
  HasErrors = "hasErrors",
  IneligibleSchoolChoice = "ineligibleSchoolChoice",
  Visibility = "visibility",
  StatusDate = "statusDate",
  SubStatusDate = "subStatusDate",
  ExpiresAtDate = "expiresAtDate",
}

export enum AttendanceTypes {
  Attending = "attending",
  Applying = "applying",
}

// for inserting parameters into URL string
// Note: When adding a new type, also consider whether it should be added to:
// - the FILTER_TYPES constant, or
// - the PARAMS_TO_SAVE constant (in hooks/useSavedViews.ts) for other features
//   that should be included in saved views.
export enum SearchAndFilterTypes {
  Search = "search",
  EnrollmentPeriod = "enrollmentPeriod",
  FormTemplate = "formTemplateId",
  Limit = "limit",
  Offset = "offset",
  SortKey = "sortKey",
  SortType = "sortType",
  // List of ids to message communication
  Recipients = "recipients",
  RelatedTo = "relatedTo",
  Forms = "forms",
  Badges = "badges",
  // Scheduled export id for editing scheduled exports
  ScheduledExportId = "scheduledExportId",
  // Tab type on the admission list view
  Tab = "tab",
  DropoffFormTemplateId = "dropoffFormTemplateId",
  FormImportId = "formImportId",
  // Saved view as entry point to form list
  SavedView = "savedView",
  // Column visibility
  VisibleColumns = "visibleColumns",
  // Remaining are the filters on the admission list view.
  // Make sure to also add these to the FILTER_TYPES constant
  Status = "status",
  SubmittedBefore = "submittedBefore",
  School = "school",
  AttendingSchool = "attendingSchool",
  Rank = "rank",
  Tag = "tag",
  VerificationStatus = "verificationStatus",
  VerificationIds = "verificationIds",
  DynamicQuestions = "dynamicQuestions",
  Grades = "grades",
  SubStatus = "subStatus",
  WithinCapacity = "withinCapacity",
  PreferredLanguage = "preferredLanguage",
  HasErrors = "hasErrors",
  IneligibleSchoolChoice = "ineligibleSchoolChoice",
  Visibility = "visibility",
  Attendance = "attendance",
  DaysAgo = "daysAgo",
  DateFrom = "dateFrom",
  DateTo = "dateTo",
  // Date filters for status/sub-status (names must match form-service API query params)
  StatusDaysAgo = "statusDaysAgo",
  StatusDateFrom = "statusDateFrom",
  StatusDateTo = "statusDateTo",
  SubStatusDaysAgo = "subStatusDaysAgo",
  SubStatusDateFrom = "subStatusDateFrom",
  SubStatusDateTo = "subStatusDateTo",
  ExpiresAtNextDays = "expiresAtNextDays",
  ExpiresAtDateFrom = "expiresAtDateFrom",
  ExpiresAtDateTo = "expiresAtDateTo",
  ExpiresAtDate = "expiresAtDate",
}

// for persisting form state on the client
export enum ClientStorageTypes {
  EnrollmentPeriod = "enrollmentPeriod",
  FormTemplate = "formTemplate",
  Attendance = "attendance",
  FormActivityLastViewed = "formActivityLastViewed",
}

// @deprecated with SmsInputStats
export const MESSAGE_CONTENT_LIMIT = 320;

export const MB_SIZE = 1024 * 1024;
export const MAX_EMAIL_ATTACHMENTS_SIZE_MB = 18;

export const TAG_NAME_LIMIT = 25;

export const PERMISSION_LEVELS: PermissionLevelsTypes = {
  [GQL.person_type_enum.admin]: {
    label: "INTERNAL ONLY",
    longLabel: "INTERNAL ONLY",
    variant: "solid",
    colorScheme: "gray",
  },
  // We are not using right now, but we need to satisfy the type system.
  [GQL.person_type_enum.applicant]: {
    label: "Internal Only",
    longLabel: "Internal only",
    colorScheme: "gray",
  },
  [GQL.person_type_enum.guardian]: {
    label: "Internal Only",
    longLabel: "Internal only",
    colorScheme: "gray",
  },
  [GQL.person_type_enum.orgAdmin]: {
    label: "Internal Only",
    longLabel: "Internal only",
    colorScheme: "gray",
  },
  [GQL.person_type_enum.districtAdmin]: {
    label: "Internal Only",
    longLabel: "Internal only",
    colorScheme: "gray",
  },
  [GQL.person_type_enum.schoolAdmin]: {
    label: "Internal Only",
    longLabel: "Internal only",
    colorScheme: "gray",
  },
};

export const MAX_FILE_SIZE_IN_MB = 10;
export const MAX_NUMBER_OF_FILES = 5;

export const UPLOAD_FILE_STATE = {
  error: {
    colorScheme: "red",
    messages: {
      FILE_TOO_LARGE: `File is larger than ${MAX_FILE_SIZE_IN_MB} MB`,
      NETWORK_ERROR: "Network error, check your connection",
      UPLOAD_ERROR: "Upload failed",
      UPLOAD_ERROR_DEFAULT: "Upload failed, try again",
    },
  },
  success: {
    colorScheme: "green",
    messages: {
      UPLOAD_SUCCESS: "Upload complete!",
    },
  },
  loading: {
    colorScheme: "blue",
    messages: {
      UPLOADING: "Uploading",
    },
  },
};

export const GENERATE_DOWNLOAD_URL = "generate-download-url";
export const GENERATE_UPLOAD_URL = "generate-upload-url";
export const GENERATE_DELETE_URL = "generate-delete-url";

export const FORM_STATUS_DESCRIPTION_MAX_CHARS = 210;
export const FORM_DESCRIPTION_MAX_CHARS = 50;

export const OFFER_STATUS: Record<GQL.offer_status_enum, FormStatusType> = {
  [GQL.offer_status_enum.Offered]: {
    label: "Offered",
    longLabel: "Offer has been made",
    icon: RiLogoutBoxRLine,
    colorScheme: "blue",
    isActive: true,
  },
  [GQL.offer_status_enum.Accepted]: {
    label: "Accepted",
    longLabel: "Offer has been accepted",
    icon: RiCheckLine,
    colorScheme: "green",
    isActive: true,
  },
  [GQL.offer_status_enum.Declined]: {
    label: "Declined",
    longLabel: "Offer has been declined",
    icon: RiCloseCircleLine,
    colorScheme: "gray",
    isActive: true,
  },
  [GQL.offer_status_enum.Revoked]: {
    label: "Revoked",
    longLabel: "Offer has been revoked",
    icon: RiCloseCircleLine,
    colorScheme: "gray",
    isActive: true,
  },
};

export const WAITLIST_STATUS: Record<GQL.waitlist_status_enum, FormStatusType> =
  {
    [GQL.waitlist_status_enum.Waitlisted]: {
      label: "Waitlisted",
      longLabel: "Form has been waitlisted",
      icon: RiTimeLine,
      colorScheme: "orange",
      isActive: true,
    },
    [GQL.waitlist_status_enum.Removed]: {
      label: "Removed",
      longLabel: "Form has been removed from waitlist",
      icon: RiCloseCircleLine,
      colorScheme: "gray",
      isActive: true,
    },
    [GQL.waitlist_status_enum.Withdrawn]: {
      label: "Withdrawn",
      longLabel: "Form has been withdrawn from waitlist",
      icon: RiCloseCircleLine,
      colorScheme: "gray",
      isActive: true,
    },
  };

export type FormSchoolRankSubStatusIndex =
  | GQL.offer_status_enum
  | GQL.waitlist_status_enum
  | GQL.form_school_rank_status_enum;

export const FORM_SCHOOL_RANK_SUB_STATUS: Record<
  FormSchoolRankSubStatusIndex,
  FormStatusType
> = {
  ...OFFER_STATUS,
  ...WAITLIST_STATUS,
  [GQL.form_school_rank_status_enum.NotConsidered]: {
    label: "Not considered",
    longLabel: "Form not considered for the lottery",
    variant: "outline",
    colorScheme: "gray",
    isActive: true,
  },
};

export enum FormTabsTypes {
  Submissions = "submissions",
  Waitlists = "waitlists",
  Offers = "offers",
  All = "all",
  FormListImports = "form-list-imports",
  DropoffForms = "dropoff-forms",
}

export type Tab = FormTab | DropoffFormTab;
export type FormTab = {
  readonly type: Exclude<FormTabsTypes, FormTabsTypes.DropoffForms>;
  readonly key: string;
  readonly label: string;
  isVisible: boolean;
};

export type DropoffFormTab = {
  readonly type: FormTabsTypes.DropoffForms;
  readonly key: string;
  readonly label: string;
  readonly formTemplateId: uuid;
  readonly isVisible: boolean;
};

export const ADMISSION_TABS: [FormTab, ...FormTab[]] = [
  {
    type: FormTabsTypes.All,
    label: "All",
    key: FormTabsTypes.All,
    isVisible: true,
  },
  {
    type: FormTabsTypes.Submissions,
    label: "Submissions",
    key: FormTabsTypes.Submissions,
    isVisible: true,
  },
  {
    type: FormTabsTypes.Waitlists,
    label: "Waitlists",
    key: FormTabsTypes.Waitlists,
    isVisible: true,
  },
  {
    type: FormTabsTypes.Offers,
    label: "Offers",
    key: FormTabsTypes.Offers,
    isVisible: true,
  },
  {
    type: FormTabsTypes.FormListImports,
    label: "Imports",
    key: FormTabsTypes.FormListImports,
    isVisible: false,
  },
];

export function getAdmissionTab(key: FormTabsTypes) {
  return ADMISSION_TABS.find((tab) => tab.key === key);
}

export function getAdmissionTabByString(keyString: string) {
  return getAdmissionTab(keyString as FormTabsTypes);
}

export const GENERIC_ERROR = {
  title: "Oops! Something went wrong!",
  description: "Check your content or network and try again later",
};

export enum VisibilityOptions {
  Visible = "visible",
  NotVisible = "notVisible",
}

export const VISIBILITY_FILTER_OPTIONS = [
  {
    label: "Visible",
    key: VisibilityOptions.Visible,
  },
  {
    label: "Not visible",
    key: VisibilityOptions.NotVisible,
  },
];

export enum SubmittedBeforeOptions {
  SubmittedBefore = "submittedBefore",
  NotSubmittedBefore = "notSubmittedBefore",
}

export const SUBMITTED_BEFORE_FILTER_OPTIONS = [
  {
    label: "Submitted before",
    key: SubmittedBeforeOptions.SubmittedBefore,
  },
  {
    label: "Not submitted before",
    key: SubmittedBeforeOptions.NotSubmittedBefore,
  },
];

export enum FileUploadTypes {
  HasFiles = "hasFiles",
  NoFiles = "noFiles",
}

export const FILE_UPLOAD_FILTER_OPTIONS = [
  {
    label: "Has uploaded files",
    id: FileUploadTypes.HasFiles,
  },
  {
    label: "No uploaded files",
    id: FileUploadTypes.NoFiles,
  },
];

export enum HasErrorsOptions {
  HasErrors = "hasErrors",
  NoErrors = "noErrors",
}

export const HAS_ERRORS_FILTER_OPTIONS = [
  {
    label: "Has errors",
    id: HasErrorsOptions.HasErrors,
  },
  {
    label: "No errors",
    id: HasErrorsOptions.NoErrors,
  },
];

export enum MessageTemplateCategory {
  General = "general",
  LotteryAndOffers = "lottery-and-offers",
}

export const MESSAGE_TEMPLATE_CATEGORIES = [
  {
    label: "General messages",
    key: MessageTemplateCategory.General,
    messageTemplateTypes: [
      GQL.message_template_type_enum.FormStarted,
      GQL.message_template_type_enum.FormSubmitted,
      GQL.message_template_type_enum.FormAdminCancelled,
      GQL.message_template_type_enum.FormCancelled,
      GQL.message_template_type_enum.FormDeleted,
      GQL.message_template_type_enum.FormVerified,
    ],
  },
  {
    label: "Lottery & offers messages",
    key: MessageTemplateCategory.LotteryAndOffers,
    messageTemplateTypes: [
      GQL.message_template_type_enum.Waitlisted,
      GQL.message_template_type_enum.WaitlistRemoved,
      GQL.message_template_type_enum.WaitlistWithdrawn,
      GQL.message_template_type_enum.OfferExtended,
      GQL.message_template_type_enum.OfferAccepted,
      GQL.message_template_type_enum.OfferDeclined,
      GQL.message_template_type_enum.OfferRevoked,
    ],
  },
];

export enum MessageTemplateSpecificity {
  Standard = "standard",
  SchoolSpecific = "school-specific",
}
type FilterTypeSettings = {
  default?: string;
  /** The number of items in the filter (or a function to calculate it). */
  count?: number | ((str: string) => number);
};
export const FILTER_DELIMITER = "_";
export const FILTER_NEGATION_PREFIX = "-";
export const FILTER_TYPES: Partial<
  Record<SearchAndFilterTypes, FilterTypeSettings>
> = {
  [SearchAndFilterTypes.Status]: {},
  [SearchAndFilterTypes.SubmittedBefore]: {},
  [SearchAndFilterTypes.School]: {},
  [SearchAndFilterTypes.AttendingSchool]: {},
  [SearchAndFilterTypes.VerificationStatus]: {},
  [SearchAndFilterTypes.VerificationIds]: { count: 0 },
  [SearchAndFilterTypes.Grades]: {},
  [SearchAndFilterTypes.DynamicQuestions]: {
    count: (questionFilter: string) => {
      if (!questionFilter) return 0;
      const questionIds = questionFilter
        .split(FILTER_DELIMITER)
        .map((question) => question.split("~")[1]);
      return uniq(questionIds).length;
    },
  },
  [SearchAndFilterTypes.SubStatus]: {},
  [SearchAndFilterTypes.Rank]: {},
  [SearchAndFilterTypes.Tag]: {},
  [SearchAndFilterTypes.WithinCapacity]: {},
  [SearchAndFilterTypes.PreferredLanguage]: {},
  [SearchAndFilterTypes.HasErrors]: {},
  [SearchAndFilterTypes.IneligibleSchoolChoice]: {},
  [SearchAndFilterTypes.Attendance]: { count: 0 },
  [SearchAndFilterTypes.Visibility]: { default: VisibilityOptions.Visible },
  [SearchAndFilterTypes.StatusDaysAgo]: {},
  [SearchAndFilterTypes.StatusDateFrom]: {},
  [SearchAndFilterTypes.StatusDateTo]: {},
  [SearchAndFilterTypes.SubStatusDaysAgo]: {},
  [SearchAndFilterTypes.SubStatusDateFrom]: {},
  [SearchAndFilterTypes.SubStatusDateTo]: {},
  [SearchAndFilterTypes.ExpiresAtNextDays]: {},
  [SearchAndFilterTypes.ExpiresAtDateFrom]: {},
  [SearchAndFilterTypes.ExpiresAtDateTo]: {},
  [SearchAndFilterTypes.ExpiresAtDate]: {},
};

interface MessageTemplate {
  label: string;
}

type MessageTemplates = Record<GQL.message_template_type_enum, MessageTemplate>;

export const MESSAGE_TEMPLATES: MessageTemplates = {
  // deprecated
  [GQL.message_template_type_enum.FormAdminCancelled]: {
    label: "Form admin canceled",
  },
  [GQL.message_template_type_enum.FormCancelled]: {
    label: "Form canceled",
  },
  [GQL.message_template_type_enum.FormDeleted]: {
    label: "Form deleted",
  },
  [GQL.message_template_type_enum.FormStarted]: {
    label: "Form started",
  },
  [GQL.message_template_type_enum.FormSubmitted]: {
    label: "Form submitted",
  },
  [GQL.message_template_type_enum.OfferAccepted]: {
    label: "Offer accepted",
  },
  [GQL.message_template_type_enum.OfferDeclined]: {
    label: "Offer declined",
  },
  [GQL.message_template_type_enum.OfferExtended]: {
    label: "Offer extended",
  },
  [GQL.message_template_type_enum.OfferRevoked]: {
    label: "Offer revoked",
  },
  [GQL.message_template_type_enum.Waitlisted]: {
    label: "Waitlisted",
  },
  [GQL.message_template_type_enum.WaitlistWithdrawn]: {
    label: "Waitlist withdrawn",
  },
  [GQL.message_template_type_enum.WaitlistRemoved]: {
    label: "Waitlist removed",
  },
  [GQL.message_template_type_enum.FormVerified]: {
    label: "Form verified",
  },
};

interface AnnouncementType {
  label: string;
  value: GQL.announcement_type_enum;
}

type AnnouncementTypes = Record<GQL.announcement_type_enum, AnnouncementType>;

export const ANNOUNCEMENT_TYPES: AnnouncementTypes = {
  [GQL.announcement_type_enum.INFO]: {
    label: "Informational",
    value: GQL.announcement_type_enum.INFO,
  },
};

interface AnnouncementEntryPoint {
  label: string;
  value: GQL.announcement_entry_point_enum;
}

type AnnouncementEntryPoints = Record<
  GQL.announcement_entry_point_enum,
  AnnouncementEntryPoint
>;

export const ANNOUNCEMENT_ENTRY_POINTS: AnnouncementEntryPoints = {
  [GQL.announcement_entry_point_enum.ADMIN_PORTAL]: {
    label: "Admin portal",
    value: GQL.announcement_entry_point_enum.ADMIN_PORTAL,
  },
  [GQL.announcement_entry_point_enum.PARENT_PORTAL]: {
    label: "Family portal",
    value: GQL.announcement_entry_point_enum.PARENT_PORTAL,
  },
};

export enum RelationshipTypes {
  Guardian = "guardian",
  Student = "student",
  Sibling = "sibling",
}

export type RelationshipPerson = {
  id: uuid;
  reference_id: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  birth_date: string | null;
  person_type: GQL.person_type_enum | null;
  applicant_attending_schools?: GQL.GetPersonRelationships_relationship_person_first_applicant_attending_schools[];
  latest_forms?: GQL.GetPersonRelationships_relationship_person_first_latest_forms[];
};

export const CQT_NO_GUARDIAN_DISABLED_REASON =
  "Answer can’t be edited because there is no Parent or Guardian on the form";

export * as Date from "./date";

export const ZIndex = {
  AdminFormButtons: 99,
  Popover: 100,
};
